import { normalizeAutomationTestId } from "../../components/InputField/testId";
import { FORM_TYPE, getFormTypeLabel } from "../../util/applicationLabels";
import {
  AUTH_V2_FORM_TYPES,
  LOGIN_V2_DECISION,
  normalizeFormType,
  toKebabCase,
} from "./loginV2Decision";

const EDD_PERIODIC_REVIEW_ROUTE_CONTEXT = {
  "periodic-review-individual-plus": {
    formType: AUTH_V2_FORM_TYPES.INDIVIDUAL_PLUS,
  },
  "periodic-review-individual-prime": {
    formType: AUTH_V2_FORM_TYPES.INDIVIDUAL_PRIME,
  },
  "periodic-review-business-plus": {
    formType: AUTH_V2_FORM_TYPES.BUSINESS_PLUS,
    type: 1,
  },
  "periodic-review-business-prime": {
    formType: AUTH_V2_FORM_TYPES.BUSINESS_PRIME,
    type: 3,
  },
};

const EDD_ROUTE_BY_FORM_TYPE = {
  [FORM_TYPE.IndividualPlus]: "periodic-review-individual-plus",
  [FORM_TYPE.IndividualPrime]: "periodic-review-individual-prime",
  [FORM_TYPE.BusinessPlus]: "periodic-review-business-plus",
  [FORM_TYPE.BusinessPrime]: "periodic-review-business-prime",
};

const getEddRequestId = (request = {}) => (
  request?.eddRequest_id ||
  request?.eddRequestId ||
  request?._id ||
  ""
);

export const normalizeEddRequestOption = (request = {}) => {
  const eddRequestId = getEddRequestId(request);
  const applicationId = request?.application_id || request?.applicationId || "";
  const formType = normalizeFormType(request?.formType);
  const dueDate = request?.dueDate ?? "";
  const dueDateKey = dueDate ? `due-${toKebabCase(String(dueDate), "unknown")}` : "due-unavailable";
  const applicationStateKey = applicationId ? "application-ready" : "application-pending";

  return {
    ...request,
    eddRequest_id: eddRequestId,
    eddRequestId,
    application_id: applicationId,
    applicationId,
    formType,
    dueDate,
    logicalKey: `periodic-review-${toKebabCase(formType, "request")}-${dueDateKey}-${applicationStateKey}`,
  };
};

export const getEddRequestOptions = (eddRequests = []) => (
  Array.isArray(eddRequests)
    ? eddRequests.map(normalizeEddRequestOption).filter((request) => Boolean(request.eddRequestId))
    : []
);

export const getEddRequestTestKey = (request = {}) => (
  normalizeAutomationTestId(normalizeEddRequestOption(request).logicalKey, "periodic-review-request")
);

export const getEddPeriodicReviewRouteContext = (path = "") => {
  const normalizedPath = String(path || "").replace(/^\//, "");
  const routeKey = Object.keys(EDD_PERIODIC_REVIEW_ROUTE_CONTEXT).find((key) => (
    normalizedPath === key || normalizedPath.startsWith(`${key}/`)
  ));

  return routeKey ? EDD_PERIODIC_REVIEW_ROUTE_CONTEXT[routeKey] : null;
};

export const buildEddLoginV2Decision = ({ eddRequests = [] } = {}) => {
  const requestOptions = getEddRequestOptions(eddRequests);
  const decisionBase = {
    eddRequests: requestOptions,
  };

  if (requestOptions.length === 1) {
    return {
      ...decisionBase,
      type: LOGIN_V2_DECISION.AUTO_RESUME,
      selectedEddRequest: requestOptions[0],
    };
  }

  if (requestOptions.length > 1) {
    return {
      ...decisionBase,
      type: LOGIN_V2_DECISION.SHOW_CHOOSER,
    };
  }

  return {
    ...decisionBase,
    type: LOGIN_V2_DECISION.BLOCKED,
  };
};

export const buildEddLoginData = ({ data = {}, otpId } = {}) => ({
  otpId,
  userType: "customer",
  email: data?.email || "",
  otpValue: data?.otpValue,
});

export const buildFinalEddAuthNavigationTarget = ({ response, eddRequestOption = {} } = {}) => {
  const normalizedRequest = normalizeEddRequestOption(eddRequestOption);
  const userContent = response?.data?.userContent || {};
  const applicationId = (
    userContent?.application_id ||
    userContent?._id ||
    normalizedRequest.applicationId ||
    ""
  );
  const responseFormType = normalizeFormType(userContent?.formType);
  const formType = EDD_ROUTE_BY_FORM_TYPE[responseFormType]
    ? responseFormType
    : normalizedRequest.formType;
  const routePrefix = EDD_ROUTE_BY_FORM_TYPE[formType];

  if (!applicationId || !routePrefix) {
    return null;
  }

  return {
    url: `/${routePrefix}/${applicationId}`,
    config: { replace: true },
    formType,
  };
};

export const getEddRequestTitle = (request = {}) => {
  const normalizedRequest = normalizeEddRequestOption(request);
  return (
    normalizedRequest.businessName ||
    normalizedRequest.accountName ||
    getFormTypeLabel(normalizedRequest.formType)
  );
};

export const formatEddDueDate = (dueDate) => {
  if (!dueDate) {
    return "";
  }

  const value = String(dueDate).trim();
  let date = null;

  if (/^\d{8}$/.test(value)) {
    const year = Number(value.slice(0, 4));
    const month = Number(value.slice(4, 6)) - 1;
    const day = Number(value.slice(6, 8));
    date = new Date(Date.UTC(year, month, day));
  } else if (/^\d+$/.test(value)) {
    const numericValue = Number(value);
    const timestamp = value.length <= 10 ? numericValue * 1000 : numericValue;
    date = new Date(timestamp);
  }

  if (!date || Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const buildEddBlockedMessage = () => (
  "No periodic reviews are available for this email."
);
